import { NotificationDeviceType } from '@common/interfaces/user'

/**
 * iPhone / iPad, including iPadOS 13+, which reports itself as a Mac and has to be told
 * apart from a real one by the presence of a touchscreen.
 *
 * A user-agent test, which is normally the wrong tool — but both callers are asking about
 * WebKit-on-Apple platform behaviour that no feature detection exposes: whether web push
 * needs the app installed first, and whether the platform already runs an edge-swipe back.
 */
export const isIosDevice = (): boolean =>
  /iphone|ipad|ipod/i.test(navigator.userAgent) ||
  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)

/** Which Notification Settings column governs this device: phones and tablets are Mobile. */
export const getNotificationDeviceType = (): NotificationDeviceType =>
  isIosDevice() || /android|mobile/i.test(navigator.userAgent)
    ? NotificationDeviceType.Mobile
    : NotificationDeviceType.Desktop

/**
 * Whether the command modifier on this machine is ⌘ rather than Ctrl.
 *
 * Only ever used to *label* a shortcut — the handlers themselves accept either
 * modifier, so a wrong answer here misprints a keycap and breaks nothing.
 * `userAgentData.platform` where it exists, `navigator.platform` where it does
 * not; both are the platform string, which is what "which key does this keyboard
 * call Command" actually depends on.
 */
export const isMacPlatform = (): boolean => {
  const platform =
    (navigator as Navigator & { userAgentData?: { platform?: string } }).userAgentData?.platform ??
    navigator.platform
  return /mac/i.test(platform ?? '')
}

/** The command modifier's keycap as a shortcut label prints it: ⌘ on a Mac, Ctrl elsewhere. */
export const commandKeycap = (): string => (isMacPlatform() ? '⌘' : 'Ctrl')
